import Dexie from 'dexie';
import { config } from '../config';
import { StorageError, TouchlineError } from '../app/errors';

/**
 * **The commit seam.** SES-2: *"no acknowledgment is ever rendered for an uncommitted mutation."*
 *
 * ── The trap this module exists to close ─────────────────────────────────────
 *
 * Design §5.1 step 8 registers the acknowledgment on the underlying **`IDBTransaction`'s `complete`
 * event**, not on Dexie's transaction promise. In a real browser those are two different moments in
 * two different orders than the Node test suite shows:
 *
 * | Environment | Ordering |
 * |---|---|
 * | Chromium / WebKit | `await db.transaction(...)` resolves **first**; `complete` fires afterwards |
 * | `fake-indexeddb` | `complete` is dispatched inside the same microtask checkpoint, so it fires **first** |
 *
 * Build unit U-0 hit this by driving a real browser: a harness that sampled a variable after
 * `await db.transaction(...)` recorded nothing and every trial failed, while the Node suite was
 * green. **A green Node suite is not evidence for anything ordering-dependent** — that is why this
 * seam is a mechanism rather than a convention.
 *
 * ── The seam, and why the right thing is the easy thing ──────────────────────
 *
 * `runWrite()` is the only write-transaction entry point `data/` exports, and its returned promise
 * resolves **only after the `complete` event**. There is no exported path that hands a caller
 * Dexie's own transaction promise, so U-4's command bus cannot await the wrong thing — the wrong
 * thing is not reachable from outside this module. Callers that need the ack *inside* the commit
 * moment pass `onCommitted`, which is invoked synchronously from the `complete` listener, which is
 * exactly where design §5.1 step 9 puts the LIVE-2 flash.
 *
 * A lint rule (`ses-2/commit-gated-writes` in `eslint.config.js`) refuses a bare `.transaction(`
 * call in `app/`, `ui/`, `domain/` and `archive/`, so the seam cannot be bypassed by reaching for
 * the exported Dexie handle. `src/test/boundary.test.ts` proves it fires by feeding it a violation.
 */

interface DexieTransactionInternals {
  idbtrans?: IDBTransaction;
}

/** Live observation of one native transaction's terminal event. */
export interface CommitWatch {
  /**
   * Attaches to the current native transaction, re-attaching if Dexie swaps it.
   *
   * Must be called **inside** the transaction scope. Dexie creates `idbtrans` lazily, so this is
   * called both on scope entry and after the caller's work: attaching only on entry would leave the
   * listener on a transaction that never commits.
   */
  attach(): void;
  /** Resolves with the `now()` reading taken inside the `complete` handler. */
  readonly committed: Promise<number>;
  /** True once a native transaction has actually been observed. */
  isAttached(): boolean;
  /** Why no listener is attached yet. Carried into the timeout message. */
  diagnostic(): string;
  /** Clears the timeout timer. Idempotent; safe to call after settling. */
  dispose(): void;
}

export interface CommitWatchOptions {
  /** Clock. Injected so a test can drive it and so `data/` never hard-codes a time source. */
  now: () => number;
  /** Stall detector, not a threshold — a transaction that never commits must not hang the app. */
  timeoutMs: number;
  /** Invoked **synchronously** inside the `complete` handler. Design §5.1 step 9's ack point. */
  onCommitted?: (at: number) => void;
  /** Typed error for the stall case, so each layer keeps its own error category (NFR-8). */
  makeTimeoutError: (diagnostic: string, timeoutMs: number) => Error;
  /** Typed error for an aborted transaction. */
  makeAbortError: () => Error;
}

/**
 * Watches the native `IDBTransaction` behind the current Dexie transaction for its terminal event.
 *
 * Both this module and `harness/runner.ts` use it — deliberately. Two copies of listener-attachment
 * logic this subtle is how one of them ends up sampling the wrong moment, which is precisely the
 * defect U-0 found in a browser and could not see in Node.
 */
export function watchCommit(options: CommitWatchOptions): CommitWatch {
  let resolveComplete: (at: number) => void = () => undefined;
  let rejectComplete: (reason: Error) => void = () => undefined;
  const committed = new Promise<number>((resolve, reject) => {
    resolveComplete = resolve;
    rejectComplete = reject;
  });

  // When the scope function throws, the transaction aborts and the `abort` listener rejects this
  // promise — but the thrown error has already left the caller and nothing is awaiting this one.
  // Unhandled, it surfaces as an `unhandledrejection`, which the OBS-1 global handler would report
  // as a fatal on screen at a venue. The real error is already propagating; this marks the
  // duplicate as handled without swallowing anything (NFR-8).
  void committed.catch(() => undefined);

  let attachedTo: IDBTransaction | null = null;
  let acked = false;
  let settled = false;
  let diagnostic = 'no IDBTransaction was ever observed';

  const timer = setTimeout(() => {
    if (settled) return;
    settled = true;
    rejectComplete(options.makeTimeoutError(diagnostic, options.timeoutMs));
  }, options.timeoutMs);

  const dispose = (): void => {
    clearTimeout(timer);
  };

  return {
    attach(): void {
      const internals = Dexie.currentTransaction as unknown as DexieTransactionInternals | null;
      if (!internals) {
        diagnostic = 'Dexie.currentTransaction was null inside the transaction scope';
        return;
      }
      const idbtrans = internals.idbtrans;
      if (!idbtrans) {
        diagnostic = 'Dexie.currentTransaction.idbtrans was not yet created';
        return;
      }
      if (idbtrans === attachedTo) return;
      attachedTo = idbtrans;
      diagnostic = `listener attached (mode=${idbtrans.mode}) but "complete" never fired`;

      // `addEventListener` is additive and does not disturb Dexie's own `oncomplete` handling.
      idbtrans.addEventListener(
        'complete',
        () => {
          const at = options.now();
          if (!acked) {
            acked = true;
            options.onCommitted?.(at);
          }
          if (settled) return;
          settled = true;
          dispose();
          resolveComplete(at);
        },
        { once: true },
      );
      idbtrans.addEventListener(
        'abort',
        () => {
          if (settled) return;
          settled = true;
          dispose();
          rejectComplete(options.makeAbortError());
        },
        { once: true },
      );
    },
    committed,
    isAttached: () => attachedTo !== null,
    diagnostic: () => diagnostic,
    dispose,
  };
}

/** What a committed write transaction reports back. */
export interface CommitReceipt {
  /** The `now()` reading taken **inside** the native `complete` handler. */
  committedAt: number;
  /** `dispatch → complete`, in milliseconds. Design §5.1's `ACK_COMMIT_BUDGET_MS` measurement. */
  commitMs: number;
}

export interface RunWriteOptions {
  /**
   * Invoked synchronously from the `complete` listener — design §5.1 step 9.
   *
   * This is the only sanctioned acknowledgment point. It runs strictly after the commit and
   * strictly before `runWrite` resolves.
   */
  onCommitted?: (at: number) => void;
  /** Clock, injectable for tests. Defaults to `performance.now()`. */
  now?: () => number;
  /** Overrides the stall bound. Defaults to `COMMIT_EVENT_TIMEOUT_MS`. */
  timeoutMs?: number;
  /** Names the operation in error context, so a failure is diagnosable without a debugger. */
  label?: string;
}

export interface WriteResult<T> {
  value: T;
  receipt: CommitReceipt;
}

/**
 * Runs `work` in exactly one `readwrite` transaction and resolves **only after the commit event**.
 *
 * Design §1.1: *"the bus applies those operations in exactly one Dexie transaction, runs
 * in-transaction postconditions that abort on violation, commits, and only then renders
 * acknowledgment."* This function is the "exactly one transaction … and only then" half.
 *
 * @param db      the Dexie connection. Passed rather than reached for, so the caller's connection
 *                (main thread or the §1.2 worker's own) is always the one used.
 * @param tables  table names in the transaction scope. Naming them is not optional: an operation on
 *                a table outside the scope throws rather than silently opening a second transaction.
 * @param work    the transaction body. It may only `await` IndexedDB operations — an IndexedDB
 *                transaction dies on any non-IDB await (design §8.2).
 *
 * @throws {StorageError} if the transaction aborts, times out, or never produced a native
 *   transaction to observe. A `TouchlineError` thrown by `work` (a postcondition rejection, say) is
 *   re-thrown unchanged so the caller sees its own category, not a storage wrapper (NFR-8).
 */
export async function runWrite<T>(
  db: Dexie,
  tables: readonly string[],
  work: () => Promise<T>,
  options: RunWriteOptions = {},
): Promise<WriteResult<T>> {
  const now = options.now ?? (() => performance.now());
  const timeoutMs = options.timeoutMs ?? config.COMMIT_EVENT_TIMEOUT_MS;
  const label = options.label ?? 'write';

  const watch = watchCommit({
    now,
    timeoutMs,
    onCommitted: options.onCommitted,
    makeTimeoutError: (diagnostic, ms) =>
      new StorageError(
        `The write "${label}" never reported an IDBTransaction "complete" within ${ms} ms (${diagnostic}). Nothing may be acknowledged for it.`,
        { context: { label, tables, diagnostic } },
      ),
    makeAbortError: () =>
      new StorageError(`The write "${label}" aborted; nothing was committed.`, {
        context: { label, tables },
      }),
  });

  const dispatchAt = now();
  let value: T;
  try {
    value = await db.transaction('rw', tables as string[], async () => {
      watch.attach();
      const out = await work();
      watch.attach();
      return out;
    });
  } catch (cause) {
    watch.dispose();
    // A domain/postcondition rejection keeps its own typed category; only genuine storage failures
    // are wrapped. Neither is ever swallowed.
    if (cause instanceof TouchlineError) throw cause;
    throw new StorageError(`The write "${label}" failed and was rolled back.`, {
      cause,
      context: { label, tables },
    });
  }

  // If no native transaction was ever observed there is no `complete` to wait for, and waiting the
  // full timeout would turn a programming error into a stall. Dexie 4 creates `idbtrans` at scope
  // entry — measured, not assumed: an empty `rw` scope still commits and still reports a receipt —
  // so the reachable trigger is a missing `Dexie.currentTransaction`, which is what a broken zone
  // (an `await` on a non-Dexie promise inside the scope) produces.
  if (!watch.isAttached()) {
    watch.dispose();
    throw new StorageError(
      `The write "${label}" never produced a native IndexedDB transaction to observe, so no commit can be confirmed. Nothing may be acknowledged for it.`,
      { context: { label, tables, diagnostic: watch.diagnostic() } },
    );
  }

  const committedAt = await watch.committed;
  return { value, receipt: { committedAt, commitMs: committedAt - dispatchAt } };
}

/**
 * The guard every mutating function in `data/` runs first.
 *
 * INV-1 requires that `Observation` is append-only *on every code path*, INV-8 that a cascade is
 * one transaction, SES-2 that nothing is acknowledged before commit. All three are claims about
 * writes that happen somewhere else, and all three collapse if a repo function can be called on its
 * own and quietly open an auto-commit transaction of its own.
 *
 * With this guard, **a mutation outside an explicit transaction scope is not reachable** — it throws
 * before it writes. Combined with `runWrite()` being the only exported way to open a `readwrite`
 * scope, every committed write in the product is commit-gated by construction rather than by
 * everyone remembering.
 *
 * @throws {StorageError} when called outside a transaction.
 */
export function requireTransaction(operation: string): void {
  const current = Dexie.currentTransaction as unknown as object | null;
  if (current) return;
  throw new StorageError(
    `"${operation}" was called outside a transaction. Every mutation goes through write() so that nothing is acknowledged before it commits (SES-2).`,
    { context: { operation } },
  );
}

/**
 * Runs `work` in one `readonly` transaction — a consistent multi-table snapshot.
 *
 * No commit gating: a readonly transaction commits nothing, so there is nothing to acknowledge and
 * SES-2 does not reach it. `repos/cascade.ts` uses it so that the BAK-4 counts a user is shown come
 * from one snapshot rather than from a series of independently-timed reads.
 *
 * @throws {StorageError} wrapping any read failure with the table scope that failed.
 */
export async function runRead<T>(
  db: Dexie,
  tables: readonly string[],
  work: () => Promise<T>,
  label = 'read',
): Promise<T> {
  try {
    return await db.transaction('r', tables as string[], work);
  } catch (cause) {
    if (cause instanceof TouchlineError) throw cause;
    throw new StorageError(`The read "${label}" failed.`, { cause, context: { label, tables } });
  }
}
